import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Clock, Calendar, User, Share2, Twitter, Linkedin, Copy, Check,
  BookOpen, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { blogPosts, blogPostList } from '../data/blogPosts';
import { guides } from '../data/guides';
import NewsletterSignup from '../components/NewsletterSignup';
import AuthorBio from '../components/AuthorBio';
import LeadMagnetForm from '../components/LeadMagnetForm';
import SEOHead from '../components/SEOHead';

// Map blog post slugs → most relevant /guides/{slug} for internal SEO juice.
// Keep mappings explicit so editorial intent stays readable.
const POST_TO_GUIDE = {
  'snowflake-487k-savings-playbook': 'snowflake',
  'snowflake-cost-optimization-playbook': 'snowflake',
  'snowflake-warehouse-burning-cash-overnight': 'snowflake',
  'how-to-audit-snowflake-bill-csv': 'snowflake',
  'databricks-dbu-reduction-guide': 'databricks',
  'databricks-hidden-costs-dbu-multiplier': 'databricks',
  'how-to-audit-databricks-bill-csv': 'databricks',
  'cloud-cost-anomaly-detection-guide': 'aws',
  'anatomy-of-a-100k-aws-bill': 'aws',
  'why-67-percent-of-saas-seats-are-wasted': 'aws',
  'kubernetes-cost-allocation-guide': 'aws',
  'mongodb-atlas-cost-optimization-guide': 'aws',
  'datadog-cost-optimization-guide': 'aws',
  'bigquery-slot-tuning-guide': 'gcp',
  'postgres-rds-cost-playbook': 'aws',
  'ec2-right-sizing-playbook': 'aws',
  'lambda-cost-traps': 'aws',
  'redshift-vs-snowflake': 'aws',
  'dynamodb-hot-partition-cost-playbook': 'aws',
  'airflow-composer-cost-playbook': 'gcp',
  'how-to-audit-aws-bill-csv': 'aws',
  'how-to-audit-azure-bill-csv': 'azure',
  'how-to-audit-gcp-bill-csv': 'gcp',
  'what-is-finops-in-2026': 'aws',
  'how-cfos-read-a-cloud-bill': 'aws',
  'llm-token-pricing-guide-2026': 'aws',
};

// Cornerstones with a printable audit-checklist PDF lead magnet.
// Slug → magnet identifier in /api/lead-magnets/{slug}/manifest.
const POST_TO_LEAD_MAGNET = {
  'ec2-right-sizing-playbook': 'ec2-right-sizing-checklist',
  'lambda-cost-traps': 'lambda-traps-checklist',
  'redshift-vs-snowflake': 'redshift-snowflake-worksheet',
  'dynamodb-hot-partition-cost-playbook': 'dynamodb-hot-partition-checklist',
  'airflow-composer-cost-playbook': 'airflow-composer-checklist',
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts[slug];
  const [copied, setCopied] = React.useState(false);

  useEffect(() => {
    // SEO: dynamically set title + meta description for this post
    if (post) {
      document.title = `${post.title} | CARTIEAI`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc && post.metaDescription) desc.setAttribute('content', post.metaDescription);
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = 'CARTIEAI | AI-Powered Cloud Cost Intelligence Platform';
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const url = typeof window !== 'undefined' ? window.location.href : `https://cartieai.com/blog/${post.slug}`;
  const canonical = `https://cartieai.com/blog/${post.slug}`;
  const suggestedGuide = guides[POST_TO_GUIDE[post.slug]];
  const leadMagnetSlug = POST_TO_LEAD_MAGNET[post.slug];

  // JSON-LD BlogPosting schema for organic search rich-result eligibility.
  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    datePublished: post.date,
    author: {
      '@type': 'Person',
      name: post.author,
      jobTitle: post.authorRole,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CARTIEAI',
      logo: {
        '@type': 'ImageObject',
        url: 'https://cartieai.com/icons/cartie-logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    keywords: post.keywords,
  };

  const onShareTwitter = () => {
    const tweet = `${post.title} via @cartieai`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(url)}`, '_blank');
  };
  const onShareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  // Pick 3 related posts (excluding this one)
  const related = blogPostList.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="blog-post-page">
      <SEOHead
        title={post.title}
        description={post.metaDescription || post.excerpt}
        canonical={canonical}
        keywords={post.keywords}
        ogType="article"
        ogSlug={post.slug}
        schema={blogPostingJsonLd}
      />
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-400 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white" data-testid="blog-post-back">
            <ArrowLeft className="w-4 h-4" />
            All posts
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/icons/cartie-emblem.png" alt="" className="w-7 h-7" />
            <span className="font-bold text-slate-900 dark:text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="mb-6 flex items-center gap-3 text-xs text-slate-400 dark:text-slate-400">
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">{post.category}</Badge>
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight mb-6" data-testid="blog-post-title">
          {post.title}
        </h1>

        <p className="text-lg text-slate-400 dark:text-slate-300 leading-relaxed mb-8">
          {post.excerpt}
        </p>

        {/* Author + share row */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold">
              {post.author[0]}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{post.author}</p>
              <p className="text-xs text-slate-400 dark:text-slate-400">{post.authorRole}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-slate-400">
            <span className="text-xs mr-1 hidden sm:inline">Share:</span>
            <button onClick={onShareTwitter} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Share on X" data-testid="blog-share-twitter">
              <Twitter className="w-4 h-4" />
            </button>
            <button onClick={onShareLinkedIn} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Share on LinkedIn" data-testid="blog-share-linkedin">
              <Linkedin className="w-4 h-4" />
            </button>
            <button onClick={onCopy} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Copy link" data-testid="blog-share-copy">
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </article>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <div className="prose prose-slate dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:tracking-tight
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
          prose-p:text-base prose-p:leading-relaxed prose-p:text-slate-700 dark:prose-p:text-slate-300
          prose-li:text-slate-700 dark:prose-li:text-slate-300
          prose-strong:text-slate-900 dark:prose-strong:text-white
          prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
          prose-blockquote:border-l-4 prose-blockquote:border-emerald-500 prose-blockquote:bg-emerald-50/50 dark:prose-blockquote:bg-emerald-900/10 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r prose-blockquote:not-italic
          prose-code:before:content-none prose-code:after:content-none prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:font-medium
          prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:rounded-xl prose-pre:p-4 prose-pre:text-sm
          prose-table:text-sm prose-th:text-slate-900 dark:prose-th:text-white prose-th:bg-slate-50 dark:prose-th:bg-slate-800/50
          prose-hr:border-slate-200 dark:prose-hr:border-slate-800
        " data-testid="blog-post-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>
      </article>

      {/* Lead-magnet PDF (cornerstones with audit checklists only) */}
      {leadMagnetSlug && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-4">
          <LeadMagnetForm slug={leadMagnetSlug} postSlug={post.slug} />
        </section>
      )}

      {/* Suggested Guide CTA — internal SEO juice + reader value */}
      {suggestedGuide && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
          <Link
            to={`/guides/${suggestedGuide.slug}`}
            data-testid="blog-suggested-guide"
            className="block group rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 hover:border-emerald-500/40 dark:hover:border-emerald-500/40 transition-all p-5 sm:p-6"
          >
            <div className="flex items-center gap-2 mb-2 text-[11px] font-bold tracking-[0.18em] text-emerald-600 dark:text-emerald-400 uppercase">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Go deeper · Field guide</span>
            </div>
            <div className="flex items-start gap-4">
              <div className="text-2xl sm:text-3xl flex-shrink-0">{suggestedGuide.icon}</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  {suggestedGuide.title}
                </h3>
                <p className="text-sm text-slate-400 dark:text-slate-400 line-clamp-2 mb-2">
                  {suggestedGuide.intro.slice(0, 160)}…
                </p>
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 group-hover:gap-2 transition-all">
                  <span>Read the {suggestedGuide.name} guide</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* CTA: Run Snowflake Health Score */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <div className="rounded-2xl bg-emerald-50 bg-gradient-to-br from-emerald-50 via-white to-sky-50 dark:from-emerald-900/20 dark:via-slate-900 dark:to-sky-900/20 border border-emerald-200 dark:border-emerald-800/40 p-6 sm:p-8 text-center">
          <p className="text-[11px] font-bold tracking-[0.2em] text-emerald-600 dark:text-emerald-400 mb-3">FREE — NO SIGNUP — 60 SECONDS</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-3">
            Find your Snowflake waste right now.
          </h2>
          <p className="text-slate-400 dark:text-slate-300 mb-6 max-w-md mx-auto">
            Take the free 10-question Snowflake Cost Health Score. Get a grade, your monthly $-waste estimate, and the top 3 fixes — instantly.
          </p>
          <Link to="/snowflake-health-score" data-testid="blog-post-snowflake-cta">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white">
              Run my free health score →
            </Button>
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <NewsletterSignup variant="light" source={`blog_${post.slug}`} />
      </section>

      {/* Author bio — face & story drives 1:1 follows */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <AuthorBio post={post} />
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Keep reading</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 dark:hover:border-emerald-500/50 transition-all"
                data-testid={`blog-related-${p.slug}`}
              >
                <Badge className="mb-2 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-0 text-[11px]">{p.category}</Badge>
                <p className="font-semibold text-sm text-slate-900 dark:text-white leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-3">
                  {p.title}
                </p>
                <p className="text-xs text-slate-400 mt-2">{p.readTime}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 py-8 px-4 mt-8">
        <div className="max-w-3xl mx-auto text-center text-xs text-slate-400">
          <p>&copy; 2026 CARTIEAI · Built by founders, for founders</p>
          <p className="mt-1">
            <Link to="/about" className="text-emerald-500 hover:underline">Our Story</Link>
            {' · '}
            <Link to="/privacy-policy" className="hover:text-slate-700 dark:hover:text-slate-300">Privacy</Link>
            {' · '}
            <Link to="/contact" className="hover:text-slate-700 dark:hover:text-slate-300">Contact</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
